import { ChangeDetectionStrategy, Component, computed, input, signal, viewChild } from '@angular/core';
import { IsoDate, Range } from '@softline/core';
import {
  DEFAULT_FISCAL_YEAR_START,
  DateRangeComparisonPickerComponent,
  DateRangeComparisonValue,
  DateRangeValue,
  fromIsoDate,
  toIsoDate,
} from '@softline/ui-core/date';
import { PageFilterItem } from '../../types/page-filter-item';
import { PageFilterService } from '../../services/page-filter.service';
import { DateBetweenFilter, Filter } from '../../../types/filter';
import { createDateRangeFilter } from '../../../functions/date-filter.function';
import { readDateRangeFilter } from '../../../helper/read-date-range-filter.function';

/**
 * A pinned period-with-comparison item: the design-system comparison picker writes one between
 * node carrying `reference`. Granularity and preset are presentation state, not persisted: the
 * last applied value is kept for the session and rebuilt from the node otherwise.
 */
@Component({
  selector: 'soft-page-filter-date-range-reference-item',
  imports: [DateRangeComparisonPickerComponent],
  templateUrl: './page-filter-date-range-reference-item.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageFilterDateRangeReferenceItemComponent {
  readonly item = input.required<PageFilterItem<any, any>>();
  readonly service = input.required<PageFilterService>();

  protected readonly defaultFiscalYearStart = DEFAULT_FISCAL_YEAR_START;
  private readonly picker = viewChild(DateRangeComparisonPickerComponent);
  private readonly lastApplied = signal<DateRangeComparisonValue | null>(null);

  readonly fiscalYearStart = computed(() => this.item().date?.fiscalYearStart ?? DEFAULT_FISCAL_YEAR_START);
  readonly committed = computed(() => this.service().getSourceItemValue(this.item()) as DateBetweenFilter<any> | null);

  /** The committed period, or the source's initial one while nothing is committed - what the queries fall back to. */
  private readonly shown = computed<{ value: Range<IsoDate>; reference?: Range<IsoDate> } | null>(() => {
    const node = this.committed();
    if (node?.value) return { value: node.value, reference: node.reference };
    const item = this.item();
    const initial = readDateRangeFilter(item.source.state.config.initialValue, item.field);
    return initial ? { value: initial.range, reference: initial.reference ?? undefined } : null;
  });

  readonly pickerValue = computed<DateRangeComparisonValue>(() => {
    const node = this.shown();
    if (!node) return unsetValue();
    const last = this.lastApplied();
    if (last && sameRange(last.range, node.value) && sameOptionalRange(last.comparison, node.reference))
      return last;
    return {
      range: toDates(node.value),
      comparison: node.reference ? toDates(node.reference) : null,
      granularity: 'custom',
      preset: null,
      comparisonMode: node.reference ? 'custom' : 'none',
    };
  });

  apply(value: DateRangeComparisonValue): void {
    const item = this.item();
    const range = toIso(value.range);
    const current = this.committed();
    const node: DateBetweenFilter<any> = current
      ? { ...current, type: 'date', operator: 'between', value: range }
      : createDateRangeFilter<any>(item.field, range);
    if (value.comparison) node.reference = toIso(value.comparison);
    else delete node.reference;
    this.lastApplied.set(value);
    this.service().applyItemFilter(item, node as Filter<any>, 'source');
  }

  close(): void {
    this.picker()?.close();
  }
}

function toDates(range: Range<IsoDate>): DateRangeValue {
  return { from: fromIsoDate(range.from) ?? new Date(), to: fromIsoDate(range.to) ?? new Date() };
}

function toIso(range: DateRangeValue): Range<IsoDate> {
  return { from: toIsoDate(range.from) as IsoDate, to: toIsoDate(range.to) as IsoDate };
}

function sameRange(a: DateRangeValue, b: Range<IsoDate>): boolean {
  return toIsoDate(a.from) === b.from && toIsoDate(a.to) === b.to;
}

function sameOptionalRange(a: DateRangeValue | null, b: Range<IsoDate> | undefined): boolean {
  if (!a || !b) return !a && !b;
  return sameRange(a, b);
}

function unsetValue(): DateRangeComparisonValue {
  const today = new Date();
  return { range: { from: today, to: today }, comparison: null, granularity: 'custom', preset: null, comparisonMode: 'none' };
}
